import Ei from "@/App/Assets/Images/ei.png";
import Gluten from "@/App/Assets/Images/gluten.png";
import Lupine from "@/App/Assets/Images/lupine.png";
import Melk from "@/App/Assets/Images/melk.png";
import Mosterd from "@/App/Assets/Images/mosterd.png";
import Noten from "@/App/Assets/Images/noten.png";
import Pindas from "@/App/Assets/Images/pindas.png";
import Schaald from "@/App/Assets/Images/schaald.png";
import Selderij from "@/App/Assets/Images/selderij.png";
import Sesamzaad from "@/App/Assets/Images/sesamzaad.png";
import Soja from "@/App/Assets/Images/soja.png";
import Vis from "@/App/Assets/Images/vis.png";
import Weekdieren from "@/App/Assets/Images/weekdieren.png";
import Zwavel from "@/App/Assets/Images/zwavel.png";
import Tarwe from "@/App/Assets/Images/tarwe.png";
import { CategoryType } from "@/shared/utils/enum";
import { allergenen, asArray, getFieldByLang } from "@/shared/utils/helpers";
import { Category, IThemeItem } from "@/shared/utils/interfaces";

export const allergenIcons: Record<number, string> = {
  1: Gluten,
  2: Schaald,
  3: Ei,
  4: Vis,
  5: Pindas,
  6: Soja,
  7: Melk,
  8: Noten,
  9: Selderij,
  10: Mosterd,
  11: Sesamzaad,
  12: Zwavel,
  13: Lupine,
  14: Weekdieren,
  15: Tarwe,
};

export const posterAllergens = [...allergenen].sort((a, b) => a.id - b.id);

// A4 at 96 DPI
export const A4_WIDTH_PX = 794;
export const A4_HEIGHT_PX = 1123;

// Row capacity per page, tuned for the fixed row height in AllergenPoster
export const FIRST_PAGE_ROWS = 26;
export const NEXT_PAGE_ROWS = 32;

export type PosterRow =
  | { type: "category"; id: string; name: string; isContinued?: boolean }
  | { type: "product"; id: string; name: string; allergens: number[] };

export type PosterColors = {
  primary: string;
  primaryText: string;
  accent: string;
  accentText: string;
  category: string;
  categoryText: string;
};

export const DEFAULT_POSTER_COLORS: PosterColors = {
  primary: "#08a7f3",
  primaryText: "#ffffff",
  accent: "#08a7f3",
  accentText: "#ffffff",
  category: "#eef7fd",
  categoryText: "#0b3b57",
};

const HEX_REGEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const normalizeHex = (color?: string) => {
  if (!color || typeof color !== "string") return null;
  const match = color.trim().match(HEX_REGEX);
  if (!match) return null;
  const hex = match[1];
  return `#${
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex
  }`.toLowerCase();
};

const luminance = (hex: string) => {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a: string, b: string) => {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
};

export const readableTextColor = (background: string, preferred?: string) => {
  const bg = normalizeHex(background) ?? "#ffffff";
  const pref = normalizeHex(preferred);
  if (pref && contrast(bg, pref) >= 3) return pref;
  return luminance(bg) > 0.45 ? "#1f2933" : "#ffffff";
};

export const withAlpha = (color: string, alpha: number) => {
  const hex = normalizeHex(color) ?? DEFAULT_POSTER_COLORS.primary;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Picks poster colours from a website theme, skipping near-white colours
// that would make the header and markers disappear on paper.
export const getPosterColors = (theme?: IThemeItem): PosterColors => {
  if (!theme?.theme) return DEFAULT_POSTER_COLORS;
  const t = theme.theme;
  const isUsable = (c?: string) => {
    const hex = normalizeHex(c);
    return hex && luminance(hex) < 0.85 ? hex : null;
  };

  const primary =
    isUsable(t.nav_background_color) ??
    isUsable(t.button_background_color) ??
    isUsable(t.category_background_color) ??
    DEFAULT_POSTER_COLORS.primary;
  const accent =
    isUsable(t.button_background_color) ??
    isUsable(t.order_now_button_color) ??
    primary;
  const category = withAlpha(primary, 0.1);

  return {
    primary,
    primaryText: readableTextColor(primary, t.nav_text_color),
    accent,
    accentText: readableTextColor(accent, t.button_text_color),
    category,
    categoryText: readableTextColor("#ffffff", primary),
  };
};

export const buildPosterRows = (
  categories: Category[] = [],
  { includeInactive }: { includeInactive: boolean }
): PosterRow[] => {
  const rows: PosterRow[] = [];

  categories
    .filter((category) => category.virtual_item_type !== CategoryType.FAVORITE)
    .filter((category) => includeInactive || category.is_active !== false)
    .forEach((category) => {
      const products = (category.sub_content ?? []).filter(
        (product) => includeInactive || product.is_active !== false
      );
      if (!products.length) return;

      rows.push({
        type: "category",
        id: `c-${category.item_id}`,
        name: getFieldByLang(category.name) ?? "",
      });
      products.forEach((product) => {
        rows.push({
          type: "product",
          id: `p-${category.item_id}-${product.item_id}`,
          name: getFieldByLang(product.name) ?? "",
          allergens: (product.allergen ? asArray(product.allergen) : []).map(
            Number
          ),
        });
      });
    });

  return rows;
};

// Splits rows into A4 pages. A category header never ends a page, and a
// category that continues on a new page gets its header repeated.
export const paginatePosterRows = (rows: PosterRow[]): PosterRow[][] => {
  const pages: PosterRow[][] = [];
  let current: PosterRow[] = [];
  let lastCategory: PosterRow | null = null;

  const capacity = () => (pages.length === 0 ? FIRST_PAGE_ROWS : NEXT_PAGE_ROWS);

  rows.forEach((row) => {
    if (row.type === "category") lastCategory = row;

    const remaining = capacity() - current.length;
    const needsNewPage =
      remaining <= 0 || (row.type === "category" && remaining <= 1);

    if (needsNewPage) {
      pages.push(current);
      current = [];
      if (row.type === "product" && lastCategory?.type === "category") {
        current.push({ ...lastCategory, isContinued: true });
      }
    }
    current.push(row);
  });

  if (current.length || !pages.length) pages.push(current);
  return pages;
};
